import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { deleteReportSchedule } from 'api/reports';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface DeleteRecurringEmailDialogProps extends AlertDialog.AlertDialogRootProps {
    scheduleId: number;
}

const DeleteRecurringEmailDialog = (props: DeleteRecurringEmailDialogProps) => {
    const { children, scheduleId, ...rest } = props;
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { data: report } = useCurrentReport();
    const { success, error } = useToast();

    const { mutate: deleteRecurringSchedule } = useMutation({
        mutationFn: () => deleteReportSchedule(accountId, report.id, scheduleId),
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Failed to delete recurring email');
        },
        onSuccess: () => {
            success('Deleted recurring email successfully');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.reportSchedulesWithFilters(accountId, report.id),
                exact: false,
            });
            goBack();
        },
    });

    return (
        <AlertDialog.Root {...rest} type={'error'}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete shared report?</AlertDialog.Title>
                        <AlertDialog.Description>
                            Deleting this shared report will remove it from being sent and prevent
                            it’s schedule to be editable.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <AlertDialog.Action onClick={() => deleteRecurringSchedule()}>
                            Delete
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteRecurringEmailDialogTrigger = AlertDialog.Trigger;

const Root = DeleteRecurringEmailDialog;
const Trigger = DeleteRecurringEmailDialogTrigger;

export {
    DeleteRecurringEmailDialog,
    DeleteRecurringEmailDialogTrigger,
    //
    Root,
    Trigger,
};
